import { useOrderDetails } from '@/pages/orders/_components/order-details/OrderDetailsProvider';
import { cn } from '@/utils/tailwind';
import { AnimatePresence, motion } from 'framer-motion';
import { startTransition } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LayoutTabSection } from '../../_layouts/LayoutTabSection';
import { TitleText } from '../common/TitleText';

import { orderQueries } from '@/api';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import GlobalSpinner from '@/components/organisms/GlobalSpinner';
import { DEFAULT_PAGE_SIZE } from '@/constants/order';
import { useAppSelector } from '@/redux/store';
import { formatISOString } from '@/utils/format-iso-string';
import { useQuery } from '@tanstack/react-query';
import { membershipOrderDetailsPreviewOrderQuery } from './membership-order-details-mock';

const ORDER_TABS = {
  'new-order': 'NewOrder',
  'ready-to-ship': 'ShippingReady',
  shipping: 'Shipping',
  delivered: 'ShippingCompleted',
} as const;

const ORDER_CHANGE_TABS = {
  cancel: 'CancelRequest',
  exchange: 'ExchangeRequest',
  return: 'ReturnRequest',
} as const;

const TabSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderTab = searchParams.get('orderTab');
  const isPreviewMode = useMembershipPreviewMode();

  const user = useAppSelector((state) => state.user);

  const { startDate, endDate, setCheckedOrders } = useOrderDetails();

  const isOrder =
    orderTab === 'new-order' || orderTab === 'ready-to-ship' || orderTab === 'shipping';

  const getOrderListQuery = useQuery(
    isPreviewMode
      ? membershipOrderDetailsPreviewOrderQuery(`tab-counts-${orderTab ?? 'new-order'}`)
      : isOrder
      ? orderQueries.getOrderList({
          cplatToken: user.CplatToken,
          dateFrom: formatISOString(startDate),
          dateTo: formatISOString(endDate),
          skip: 0,
          take: DEFAULT_PAGE_SIZE,
          statusTab: ORDER_TABS[orderTab as keyof typeof ORDER_TABS],
        })
      : orderQueries.getOrderChangeList({
          cplatToken: user.CplatToken,
          dateFrom: formatISOString(startDate),
          dateTo: formatISOString(endDate),
          skip: 0,
          take: DEFAULT_PAGE_SIZE,
          statusTab: ORDER_CHANGE_TABS[orderTab as keyof typeof ORDER_CHANGE_TABS],
        }),
  );

  const counts = getOrderListQuery.data?.data[0].counts;

  const tabListWithCounts = [
    { label: '신규주문', value: 'new-order', count: counts?.newOrderCount },
    { label: '발송대기', value: 'ready-to-ship', count: counts?.shippingReadyCount },
    { label: '배송중', value: 'shipping', count: counts?.shippingCount },
    { label: '배송완료', value: 'delivered', count: counts?.shippingCompletedCount },
    { label: '취소', value: 'cancel', count: counts?.cancelRequestCount },
    { label: '교환', value: 'exchange', count: counts?.exchangeRequestCount },
    { label: '반품', value: 'return', count: counts?.returnRequestCount },
  ];

  const handleTabClick = (value: string) => {
    if (value === orderTab) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set('orderTab', value);
    setCheckedOrders([]);
    setSearchParams(newParams);
  };

  return (
    <LayoutTabSection>
      {getOrderListQuery.isPending && <GlobalSpinner className="z-[50]" />}

      <TitleText title="주문내역" />
      <div
        className="relative flex select-auto flex-wrap gap-x-[32px] gap-y-[10px]"
        data-membership-preview-interactive="true"
      >
        {tabListWithCounts.map((tab) => {
          const isActive = orderTab === tab.value;
          return (
            <button
              key={tab.value}
              className={cn(
                'group relative pb-[18px] transition-colors duration-300 Title3S18',
                'text-[clamp(14px,2.5vw,18px)] 875:text-[18px]',
                isActive ? 'text-gr-900' : 'text-gr-500 hover:text-gr-700',
              )}
              onClick={() => startTransition(() => handleTabClick(tab.value))}
            >
              <div className="flex flex-row items-center gap-2">
                {tab.label} {tab.count}
              </div>

              {/* ✅ 클릭 시 하단 라인 애니메이션 */}
              <AnimatePresence>
                {isActive && (
                  <motion.span
                    layoutId="tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-gr-800"
                    transition={{ type: 'spring', stiffness: 400, damping: 30 }}
                  />
                )}
              </AnimatePresence>
            </button>
          );
        })}
      </div>
    </LayoutTabSection>
  );
};

export default TabSection;
